import {
  DatePicker,
  InputAdornment,
  Option,
  SearchAutocomplete,
  Select,
} from "@deere/fuel-react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";
import { Clear, Search } from "@mui/icons-material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import React, { useState } from "react";
import {
  BRAZIL_CNTRY_CODE,
  DATA_ENTRY,
  DEERE_EMPLOYEE,
  DEERE_MANAGER,
  PERCENTAGE,
  SALES_MANAGER_ROLE,
  SALES_PERSON_ROLE,
  SPANISH_COUNTRIES_CODE,
} from "../../utilities/constants";
import {
  getUserObj,
  hasRole,
  sortDropdownByDisplayOrder,
} from "../../utilities/tp-validations.js";
import { avgSprdMrgPct, formatDate } from "../../utilities/utils";
import ConnectedMultiSelect from "../Utility/connected-multi-select";
import Dropdown from "../Utility/dropdown";
import ToastUtility from "../Utility/toast-utility";
import {DATA_AVAILABLE} from "../../action";

function SearchFilter(props) {
  const [advFilterDisplayStyle, setAdvFilterDisplayStyle] = useState(false);
  const isDeereEmp = hasRole(DEERE_EMPLOYEE, props);
  const isDeereManager = hasRole(DEERE_MANAGER, props);
  const [flags, setFlags] = useState({
    searchClick: false,
  });

  const [toastObj, setToastObj] = useState({
    variant: "default",
    display: "none",
    message: "",
  });
  let toastTimeout;

  const getDurationData = () => {
    const durationData = props.domainDetails && props.domainDetails["DURATION"];
    if (!durationData) {
      return [];
    }
    const sortedData = sortDropdownByDisplayOrder(durationData);
    return sortedData.map(({ domainValue }) => domainValue);
  };

  const handleChangeDuration = (e) => {
    const duration = e.target.value;
    changeDateOnDuration(duration);
  };

  function getDomainKeyForMarketSegment() {
    if (hasRole(DEERE_MANAGER, props) || hasRole(DEERE_EMPLOYEE, props)) {
      return ["MARKET_SEGMENT", "MARKET_SEGMENT_LAT", "MARKET_SEGMENT_BR"];
    }
    return getUserObj(props).countryCode === BRAZIL_CNTRY_CODE
        ? "MARKET_SEGMENT_BR"
        : SPANISH_COUNTRIES_CODE.includes(getUserObj(props).countryCode)
            ? "MARKET_SEGMENT_LAT"
            : "MARKET_SEGMENT";
  }

  const changeDateOnDuration = (newDuration) => {
    let newFromDate = "";
    let newToDate = "";
    if (newDuration !== "All" && newDuration !== "Custom Date") {
      const durationInMonths = parseInt(newDuration.slice(1));
      newFromDate = new Date();
      newFromDate.setMonth(newFromDate.getMonth() - durationInMonths);
      newToDate = new Date();
    } else if (newDuration === "Custom Date") {
      newFromDate = new Date();
      newToDate = new Date();
    }
    props.setSearchObj({
      ...props.searchObj,
      duration: newDuration,
      fromDate: newFromDate,
      toDate: newToDate,
    });
  };

  const showToast = (variant, message) => {
    clearTimeout(toastTimeout);
    setToastObj({
      variant,
      display: "inline",
      message,
    });

    toastTimeout = setTimeout(() => {
      setToastObj((prevToastObj) => ({
        ...prevToastObj,
        display: "none",
      }));
    }, 5000);
  };

  const handleSearchTransaction = async (searchObj) => {
    if (
        searchObj.duration === "Custom Date" &&
        (searchObj.fromDate === "" || searchObj.toDate === "")
    ) {
      showToast("error", "Please select to and from date");
    } else {
      setFlags((prevObj) => ({
        ...prevObj,
        searchClick: true,
      }));
      const isSalesPerson = hasRole(SALES_PERSON_ROLE, props);
      const isDataEntryUser = hasRole(DATA_ENTRY, props);
      const userDetails = getUserObj(props);
      const updatedSearchObj = {
        ...searchObj
      };
      if (searchObj.fromDate) {
        updatedSearchObj.fromDate = formatDate(searchObj.fromDate);
      }
      if (searchObj.toDate) {
        updatedSearchObj.toDate = formatDate(searchObj.toDate);
      }
      if (searchObj.avgSprdPct) {
        updatedSearchObj.avgSprdPct = avgSprdMrgPct(searchObj.avgSprdPct);
      }
      if(isSalesPerson){
        updatedSearchObj.salePersonID = userDetails.userId;
      }
      if(isDataEntryUser){
        updatedSearchObj.createByUser = userDetails.userId;
      }
      try {
        const response = await props.fetchSearchTransactions(updatedSearchObj);
        if (response) {
          setFlags((prevObj) => ({
            ...prevObj,
            searchClick: false,
          }));
        }
        props.setTableVisibility(true);
      } catch (error) {
        console.error("handleSearchTransaction ", error);
      }
    }
  };

  const handleChangeParentDealerAccount = (e) => {
    const parentDealerAccountId = e.target.value;
    props.setSearchObj({
      ...props.searchObj,
      parentDealerAccountId,
      dealerAccountId: "All",
      salePersonID: "All",
    });
    props.resetSearchTxnAccountDropdowns("storedSalesPersonNames");
    props.fetchStoredDealerAccountDetails(parentDealerAccountId);
  };

  const handleChangeDealerAccount = (e) => {
    const userDetails = getUserObj(props);
    const isDataEntryPerson = hasRole(DATA_ENTRY, props);
    const isSalesPerson = hasRole(SALES_PERSON_ROLE, props);
    const dealerAccountId = e.target.value;
    props.setSearchObj({
      ...props.searchObj,
      dealerAccountId,
      salePersonID: isSalesPerson ? userDetails.userId : "All",
    });
    const parentDealerAccountId = props.searchObj?.parentDealerAccountId || "";
    if (isDataEntryPerson)
      props.fetchSalesPersonNames(
          dealerAccountId,
          parentDealerAccountId,
          userDetails.userId
      );
    else
      props.fetchSalesPersonNames(dealerAccountId, parentDealerAccountId, "");
  };

  const createDomainDropdownOptions = (domainName) => {
    const allDropDownOption = (
        <Option key="All" value="All" selected>
          All
        </Option>
    );
    let dropdownOptions = [allDropDownOption];
    if (props.domainDetails) {
      dropdownOptions.push(
          ...props.domainDetails[domainName]?.map((domain) => (
              <Option key={domain.domainCode} value={domain.domainCode}>
                {domain.domainValue}
              </Option>
          ))
      );
    }
    return dropdownOptions;
  };

  const resetSearchFilters = () => {
    const userDetails = getUserObj(props);
    const isSalesManager = hasRole(SALES_MANAGER_ROLE, props);
    const isSalesPerson = hasRole(SALES_PERSON_ROLE, props);
    const isDataEntryPerson = hasRole(DATA_ENTRY, props);
    props.resetSearchObj({
      parentDealerAccountId:
          isSalesPerson || isSalesManager || isDataEntryPerson
              ? props.dealerAccount.group3LeadId
              : "All",
      dealerAccountId: "All",
      salePersonID: isSalesPerson ? userDetails.userId : "All",
    });
    if (isSalesPerson || isSalesManager || isDataEntryPerson) {
      if (isSalesPerson)
        props.fetchStoredDealerAccountDetails(
            props.dealerAccount.group3LeadId,
            userDetails.userId,
            true
        );
      else if (isDataEntryPerson)
        props.fetchStoredDealerAccountDetails(
            props.dealerAccount.group3LeadId,
            userDetails.userId,
            false
        );
      else
        props.fetchStoredDealerAccountDetails(props.dealerAccount.group3LeadId);
    }
  };
  return (
      <>
        <Box
            p={5}
            bgcolor={"white"}
            sx={{ borderRadius: "4px", paddingBottom: 4 }}
        >
          <Grid
              alignItems="flex-start"
              container
              spacing={6}
              p={1}
              className="mandatory-filters"
          >
            <Grid item xs={10}>
              <Typography variant="h3" component="h3">
                {props.texts.SRCH_TRANSACTIONS}
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Button
                  style={{ float: "right" }}
                  disabled={isDeereEmp || isDeereManager ?
                      false :
                      !( props.relatedAccount?.grp3LeadIdAvailable && props.searchTxnAccountDropdowns?.dealerDataAvailable)}
                  endIcon={
                    flags.searchClick ? (
                        <CircularProgress
                            size="small"
                            style={{ color: "#000", marginLeft: "7px" }}
                        />
                    ) : (
                        <Search />
                    )
                  }
                  variant="primary"
                  onClick={() => handleSearchTransaction(props.searchObj)}
              >
                {props.texts.SEARCH}
              </Button>
            </Grid>
            <Grid item xs={2}>
              <Dropdown
                  id="duration"
                  label={props.texts.DURATION}
                  isDisable={false}
                  data={getDurationData()}
                  onChangeFn={handleChangeDuration}
                  value={props.searchObj?.duration ?? "All"}
              />
            </Grid>
            <Grid item xs={2}>
              <FormControl fullWidth>
                <InputLabel htmlFor="transactionDate" placeholder="date">
                  {props.texts.FROM} {props.texts.DATE}
                </InputLabel>
                <DatePicker
                    id="fromDate"
                    value={props.searchObj?.fromDate ?? ""}
                    maxDate={
                      props.searchObj && props.searchObj.toDate
                          ? props.searchObj.toDate
                          : ""
                    }
                    onChange={(dates) => {
                      if (props.searchObj?.duration === "Custom Date")
                        props.setSearchObj({
                          ...props.searchObj,
                          fromDate:
                              dates[0] && !isNaN(new Date(dates[0]))
                                  ? new Date(dates[0])
                                  : "",
                        });
                    }}
                    readOnly={props.searchObj?.duration !== "Custom Date"}
                    InputProps={{
                      startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthIcon />
                          </InputAdornment>
                      ),
                    }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={2}>
              <FormControl fullWidth>
                <InputLabel htmlFor="transactionDate" placeholder="date">
                  {props.texts.CUSTOM_TO} {props.texts.DATE}
                </InputLabel>
                <DatePicker
                    id="toDate"
                    value={props.searchObj?.toDate ?? ""}
                    minDate={props.searchObj?.fromDate}
                    onChange={(dates) => {
                      if (props.searchObj?.duration === "Custom Date")
                        props.setSearchObj({
                          ...props.searchObj,
                          toDate:
                              dates[0] && !isNaN(new Date(dates[0]))
                                  ? new Date(dates[0])
                                  : "",
                        });
                    }}
                    readOnly={props.searchObj?.duration !== "Custom Date"}
                    InputProps={{
                      startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthIcon />
                          </InputAdornment>
                      ),
                    }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={2}>
              <Button
                  style={{
                    top: "25px",
                    left: "25px",
                  }}
                  size="large"
                  variant="text-inline"
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={() => setAdvFilterDisplayStyle(!advFilterDisplayStyle)}
              >
                {props.texts?.ADV_CRITERIA}
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button
                  style={{
                    float: "right",
                    margin: "25px",
                  }}
                  size="large"
                  variant="text-inline"
                  endIcon={<Clear />}
                  onClick={resetSearchFilters}
              >
                {props.texts?.CLEAR_ALL}
              </Button>
            </Grid>
          </Grid>
          {advFilterDisplayStyle && (
              <div>
                <Grid
                    alignItems="flex-start"
                    container
                    spacing={6}
                    p={1}
                    className="mandatory-filters"
                >
                  <Grid item xs={12}>
                    <Typography variant="h5" component="h5">
                      {`${props.texts.SRCH_BY} ${props.texts.GNRL_DTLS}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} marginTop={"-15px"}>
                    <TextField
                        label={props.texts.NAME}
                        type="text"
                        placeholder={props.texts.STARTS_WITH_PH}
                        value={props.searchObj?.customerName ?? ""}
                        onChange={(e) =>
                            props.setSearchObj({
                              ...props.searchObj,
                              customerName: e.target.value,
                            })
                        }
                    />
                  </Grid>
                  <Grid item xs={2} marginTop={"-20px"}>
                    <ConnectedMultiSelect
                        searchObjField="competitors"
                        transKey="COMPETITOR"
                        domainKey="COMP_MAKE"
                    />
                  </Grid>
                </Grid>
                <Grid
                    alignItems="flex-start"
                    container
                    spacing={6}
                    p={1}
                    className="mandatory-filters"
                >
                  <Grid item xs={12}>
                    <Typography variant="h5" component="h5">
                      {`${props.texts.SRCH_BY} ${props.texts.DEAL_DTLS}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} marginTop={"-20px"}>
                    <ConnectedMultiSelect
                        searchObjField="dealSources"
                        transKey="DEAL_SOURCE"
                        domainKey="DEAL_SOURCE"
                    />
                  </Grid>
                  <Grid item xs={2} marginTop={"-20px"}>
                    <ConnectedMultiSelect
                        searchObjField="dealTypes"
                        transKey="DEAL_TYPE"
                        domainKey="DEAL_TYPE"
                    />
                  </Grid>
                  {hasRole(DEERE_EMPLOYEE, props) && (
                      <Grid item xs={2} marginTop={"-15px"}>
                        <Dropdown
                            id="parentDealerAccountID"
                            label={props.texts.PARENT_DEALER_ACCOUNT_ID}
                            data={
                              props.searchTxnAccountDropdowns
                                  .storedParentDealerAccountIds
                            }
                            onChangeFn={handleChangeParentDealerAccount}
                            value={props.searchObj?.parentDealerAccountId}
                        />
                      </Grid>
                  )}
                  {(hasRole(SALES_MANAGER_ROLE, props) ||
                      hasRole(SALES_PERSON_ROLE, props) ||
                      hasRole(DEERE_EMPLOYEE, props) ||
                      hasRole(DATA_ENTRY, props)) && (
                      <Grid item xs={2} marginTop={"-15px"}>
                        <Dropdown
                            id="dealerAccountId"
                            label={props.texts.ENTER_DLR_ACCT}
                            isDisable={
                              hasRole(DEERE_EMPLOYEE, props) &&
                              (!props.searchObj?.parentDealerAccountId ||
                                  props.searchObj?.parentDealerAccountId === "All")
                            }
                            data={
                              props.searchTxnAccountDropdowns.storedDealerAccountIds
                            }
                            onChangeFn={handleChangeDealerAccount}
                            value={props.searchObj?.dealerAccountId}
                        />
                      </Grid>
                  )}
                  {(hasRole(SALES_MANAGER_ROLE, props) ||
                      hasRole(DEERE_EMPLOYEE, props) ||
                      hasRole(DATA_ENTRY, props)) && (
                      <Grid item xs={2} marginTop={"-15px"}>
                        <Dropdown
                            id="salesPersonName"
                            label={props.texts.SALES_PERSON}
                            isDisable={
                              hasRole(DEERE_EMPLOYEE, props)
                                  ? !props.searchObj?.parentDealerAccountId ||
                                  !props.searchObj?.dealerAccountId ||
                                  props.searchObj?.parentDealerAccountId === "All" ||
                                  props.searchObj?.dealerAccountId === "All"
                                  : !props.searchObj?.dealerAccountId ||
                                  props.searchObj?.parentDealerAccountId === "All" ||
                                  props.searchObj?.dealerAccountId === "All"
                            }
                            data={
                              props.searchTxnAccountDropdowns.storedSalesPersonNames
                            }
                            onChangeFn={(e) =>
                                props.setSearchObj({
                                  ...props.searchObj,
                                  salePersonID: e.target.value,
                                })
                            }
                            value={props.searchObj?.salePersonID}
                        />
                      </Grid>
                  )}
                </Grid>
                <Grid
                    alignItems="flex-start"
                    container
                    spacing={6}
                    p={1}
                    className="mandatory-filters"
                >
                  <Grid item xs={12}>
                    <Typography variant="h5" component="h5">
                      {`${props.texts.SRCH_BY} ${props.texts.EQUIP_DTLS}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} marginTop={"-20px"}>
                    <ConnectedMultiSelect
                        searchObjField="marketSegments"
                        transKey="MARKET_SEGMENT"
                        domainKey={getDomainKeyForMarketSegment()}
                    />
                  </Grid>
                  <Grid item xs={2} marginTop={"-15px"}>
                    <FormControl fullWidth>
                      <InputLabel
                          htmlFor="Films"
                          id="Films-label"
                      >
                        {props.texts.MODEL_AND_CATGRY}
                      </InputLabel>
                    <SearchAutocomplete
                        InputProps={{
                          label: props.texts.MODEL_AND_CATGRY,
                          helperText:
                              props.searchObj &&
                              props.searchObj.model &&
                              props.searchObj.category
                                  ? `${props.searchObj.model} ${props.searchObj.category}`
                                  : "All",
                        }}
                        value={
                          props.searchObj &&
                          props.searchObj.model &&
                          props.searchObj.category
                              ? `${props.searchObj.model} ${props.searchObj.category}`
                              : "All"
                        }
                        isOptionEqualToValue={(option, chosen) =>
                            option.value === chosen.value
                        }
                        onChange={(e, v, reason) => {
                          if (reason && reason === "clear") {
                            props.setSearchObj({
                              ...props.searchObj,
                              model: "",
                              category: "",
                            });
                          } else if (v && v.value) {
                            const [model, category] = v.value.split("_");
                            if (model && category) {
                              props.setSearchObj({
                                ...props.searchObj,
                                model,
                                category,
                              });
                            }
                          }
                        }}
                        options={[...props.machines, { label: "All", value: "All" }]}
                    />
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid
                    alignItems="flex-start"
                    container
                    spacing={6}
                    p={1}
                    className="mandatory-filters"
                >
                  <Grid item xs={12}>
                    <Typography variant="h5" component="h5">
                      {`${props.texts.SRCH_BY} ${props.texts.SALES_DTLS}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} marginTop={"-15px"}>
                    <FormControl fullWidth>
                      <InputLabel
                          htmlFor="Films"
                          id="Films-label"
                      >
                        {props.texts.RESULT}
                      </InputLabel>
                    <Select
                        fullWidth={true}
                        id="result-select-field"
                        label={props.texts.RESULT}
                        value={props.searchObj?.result ?? "All"}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 250 } } }}
                        onChange={(e) =>
                            props.setSearchObj({
                              ...props.searchObj,
                              result: e.target.value,
                            })
                        }
                    >
                      {createDomainDropdownOptions("RESULT")}
                    </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={2} marginTop={"-20px"}>
                    <ConnectedMultiSelect
                        searchObjField="txnStatuses"
                        transKey="STATUS"
                        domainKey="TXN_STATUS"
                    />
                  </Grid>
                  <Grid item xs={2} marginTop={"-15px"}>
                    <FormControl fullWidth>
                      <InputLabel
                          htmlFor="Films"
                          id="Films-label"
                      >
                        {`${props.texts.AVERAGE_SPREAD} ${PERCENTAGE}`}
                      </InputLabel>
                    <Select
                        fullWidth={true}
                        id="result-select-field"
                        value={props.searchObj?.avgSprdPct ?? "All"}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 250 } } }}
                        onChange={(e) =>
                            props.setSearchObj({
                              ...props.searchObj,
                              avgSprdPct: e.target.value,
                            })
                        }
                    >
                      {createDomainDropdownOptions("SRCH_AVERAGE_SPREAD")}
                    </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </div>
          )}
        </Box>
        <ToastUtility
            toastDisplay={toastObj.display}
            toastVariant={toastObj.variant}
            toastMessage={toastObj.message}
        />
      </>
  );
}

export default SearchFilter;
